import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ApiService } from "src/app/api.service";
import { APIResults } from "src/app/sharedinterfaces";
import { ChartingService } from "src/app/charting.service";
import { DvsChartComponent } from "src/app/shared/dvs-chart/dvs-chart.component";
import { XAxisPlotLinesOptions } from "highcharts";
import { FlightViewer } from "src/app/flight-viewer";

@Component({
	selector: "app-flight-map",
	templateUrl: "./flight-map.component.html",
	styleUrls: ["./flight-map.component.scss"]
})
export class FlightMapComponent implements OnChanges, OnDestroy, OnInit {
	@Input()
	asset: APIResults.FleetAsset;
	@Input()
	fleet: APIResults.Fleet;
	@Input()
	operation: APIResults.Operation;

	@ViewChild("scrubberChart", { static: true })
	scrubberChart: DvsChartComponent;

	private regimes: any[] = [];

	public pathFrame: APIResults.FlightPathFrame;
	public scrubLine: XAxisPlotLinesOptions;

	public viewer: FlightViewer;

	public lockCamera = true;

	public scrubberOptions: Highcharts.Options = undefined;

	public loading = false;
	public error = undefined;

	constructor(public api: ApiService, private charting: ChartingService, private changeDetectorRef: ChangeDetectorRef) { }

	ngOnInit() {
		const viewer = new FlightViewer(document.querySelector("#flightmap"));
		this.viewer = viewer;
		this.viewer.onPathFrame = (pathFrame) => {
			this.pathFrame = pathFrame;
			// Update the line on the scrubber chart
			if (this.scrubberChart && this.scrubLine) {
				this.scrubLine.value = this.pathFrame.ts * 1000;
				this.scrubberChart.UpdatePlotLine();
				this.changeDetectorRef.detectChanges();
			}
		};
	}

	async ngOnChanges(): Promise<void> {
		this.scrubberOptions = undefined;
		this.scrubLine = undefined;
		this.loading = true;
		this.error = undefined;
		try {
			const [path, exceedances] = await Promise.all([
				this.api.GetExpandedFlightPath(this.asset.asset_id, this.operation.operation_id),
				this.api.GetExceedances(this.asset.asset_id, this.operation.operation_id),
			]);

			// Initialize the map and WebGL overlay
			await this.viewer.LoadPath(path);

			// Fetch the regimes to use later in the regime CI chart, if needed
			this.regimes = await this.api.GetRegimes(this.asset.asset_id);

			const altitudeSeries: any = {
				name: "Altitude",
				type: "line",
				zIndex: 1,
				data: path.map(point => [point.ts * 1000, point.altitude]),
				units: "ft",
				point: {
					events: {
						click: (evt) => {
							const progress = evt.point.index / path.length;
							this.viewer.SetProgress(progress);
						}
					}
				},
			};
			const timestamps = path.map(point => point.ts);

			const pointDetails = (point: Highcharts.Point) => {
				const frame = path[point.index];
				const regime_id = frame.regime_id;
				if (this.regimes && this.regimes[regime_id]) {
					return "<br/>Regime: " + this.regimes[regime_id].name;
				}
				return "";
			};
			const opt = this.charting.FlightTimeDefaults("", "", undefined, true, [altitudeSeries], timestamps, pointDetails, "Altitude", this.fleet ? this.fleet.time_offset_hours : 0);
			const xaxis: any = opt.xAxis;
			this.scrubLine = {
				value: path[0].ts * 1000,
				width: 1,
				color: "#fff",
				zIndex: 3,
			};
			// Include exceedance lines colored by severity
			// Only show an exceedance label every 5% of the flight time
			// Note that now that exceedances are collapsed, there will only be a line at the beginning of an exceedance
			let lastLabelPercent = -99;
			const totalTime = path[path.length - 1].ts * 1000 - path[0].ts * 1000;
			const exceedanceLines = [];
			for (const ex of exceedances.rows) {
				const percent = (ex.timestamp.valueOf() - path[0].ts * 1000) / totalTime;
				let label = undefined;
				if (percent - lastLabelPercent > 0.05) {
					label = {
						text: ex.name,
						align: "left",
						x: -14,
						y: 4,
						style: {
							color: ex.severity === 0 ? "#ff0" : "#f00",
							textShadow: "#000 1px 1px 2px",
						},
						visible: true,
					};
					lastLabelPercent = percent;
				}
				exceedanceLines.push({
					value: ex.timestamp.valueOf(),
					width: 2,
					color: ex.severity === 0 ? "#ff0" : "#f00",
					zIndex: 2,
					label,
				});
			}
			xaxis.plotLines = [this.scrubLine, ...exceedanceLines];
			xaxis.crosshair = true;
			opt.chart.animation = false;
			opt.chart.zooming = {
				type: undefined,
			};
			opt.tooltip.shared = true;
			if (!opt.chart.events) opt.chart.events = {};
			/* eslint-disable-next-line @typescript-eslint/no-this-alias */
			const me = this;
			opt.chart.events.click = function () {
				const progress = this.hoverPoint.index / path.length;
				me.viewer.SetProgress(progress);
			}
			this.scrubberOptions = opt;
		} catch (err) {
			this.error = err.message;
		} finally {
			this.loading = false;
		}
	}

	ngOnDestroy() {
		if (this.viewer) this.viewer.Destroy();
	}

	lookupRegime(regime_id: number): string {
		if (this.regimes && this.regimes[regime_id]) {
			return this.regimes[regime_id].name;
		}
		return "";
	}
}
